<template>
  <div class="w-100 h-100">
    <canvas
      ref="dailyBalanceGraphCanvas"
      class="h-100 w-100 daily-balance-graph-canvas"
    ></canvas>
    <DailyBalanceGraphTooltip
      v-show="showTooltip"
      ref="dailyBalanceGraphTooltipRef"
      :tooltip-data="tooltipData"
    />
  </div>
</template>

<script>
import DailyBalanceGraphTooltip from './DailyBalanceGraphTooltip.vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-moment';
import { $event } from '@/bus';
import moment from 'moment';
import { centsToCurrencyShorthand } from '@/utils/toCurrency';
import constants from './constants';
import {
  averageDatasetValue,
  downloadChart,
  downloadPDF,
  minDatasetValue,
  minGraphValue,
  maxDatasetValue,
  printChart,
  toggleFullscreen,
} from '@/utils/chartUtils';

Chart.register(annotationPlugin);

export default {
  name: 'DailyBalanceGraph',

  components: {
    DailyBalanceGraphTooltip,
  },
  props: {
    dailyBalanceAccountData: {
      type: Array,
      required: true,
    },
    accountsAggregateData: {
      type: Object,
      required: true,
    },
    selectedAccount: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showTooltip: false,
    tooltipData: {},
  }),
  computed: {
    isAllAccountsViewSelected() {
      return this.selectedAccount.id === constants.ALL_ACCOUNTS_ID;
    },
    isAggregateAccountViewSelected() {
      return this.selectedAccount.id === constants.AGGREGATE_ACCOUNTS_ID;
    },
    isSingleAccountSelected() {
      return (
        this.isAllAccountsViewSelected === false &&
        this.isAggregateAccountViewSelected === false
      );
    },
  },
  watch: {
    selectedAccount(newAccountSelected, oldAccountSelected) {
      this.handleSelectedAccountChange(newAccountSelected, oldAccountSelected);
    },
    dailyBalanceAccountData() {
      this.handleDataChange();
    },
    accountsAggregateData() {
      this.handleDataChange();
    },
  },
  created() {
    this.setupEventListeners();
  },
  beforeDestroy() {
    this.destroyEventListeners();
  },
  async mounted() {
    this.addChartStyleDataToAccountData();
    this.addChartStyleDataToAggregateAccountData();
    this.createChart();
  },
  methods: {
    addChartStyleDataToAccountData() {
      for (const [accountIndex] of Object.entries(
        Object.entries(this.dailyBalanceAccountData),
      )) {
        const account = this.dailyBalanceAccountData[accountIndex];
        const accountColour = this.getAccountColour(accountIndex);

        account.pointBorderColor = 'transparent';
        account.pointBackgroundColor = 'transparent';
        account.pointHoverBackgroundColor = accountColour;
        account.borderColor = accountColour;
        account.backgroundColor = accountColour;
        account.pointRadius = 10;
        account.fill = true;
        account.backgroundColor = this.fillRedWhenNegativeBalanceExists;
        account.spanGaps = false;
      }
    },
    addChartStyleDataToAggregateAccountData() {
      const accountColour = this.getAccountColour(0);
      this.accountsAggregateDataWithStyles = { ...this.accountsAggregateData };
      this.accountsAggregateDataWithStyles.pointBorderColor = 'transparent';
      this.accountsAggregateDataWithStyles.pointBackgroundColor = 'transparent';
      this.accountsAggregateDataWithStyles.pointHoverBackgroundColor =
        accountColour;
      this.accountsAggregateDataWithStyles.borderColor = accountColour;
      this.accountsAggregateDataWithStyles.backgroundColor = accountColour;
      this.accountsAggregateDataWithStyles.pointRadius = 10;
      this.accountsAggregateDataWithStyles.fill = true;
      this.accountsAggregateDataWithStyles.backgroundColor =
        this.fillRedWhenNegativeBalanceExists;
    },
    datasetsUsed() {
      if (this.isAllAccountsViewSelected) {
        return this.dailyBalanceAccountData;
      } else if (this.isAggregateAccountViewSelected) {
        return [this.accountsAggregateDataWithStyles];
      } else {
        return this.dailyBalanceAccountData.filter(
          (account) => account.id === this.selectedAccount.id,
        );
      }
    },
    handleSelectedAccountChange(selectedAccount, oldSelectedAccount) {
      if (selectedAccount.id === oldSelectedAccount.id) {
        return;
      }
      this.updateGraphData();
    },
    handleDataChange() {
      this.addChartStyleDataToAccountData();
      this.addChartStyleDataToAggregateAccountData();
      this.updateGraphData();
    },
    updateGraphData() {
      this.chart.data.datasets = this.datasetsUsed();
      this.forceGraphOptionsUpdate();
    },
    forceGraphOptionsUpdate() {
      // This is needed as certain configurations are not updated simply when the datasets are changed.
      this.chart.options = this.graphOptionsConfiguration();
      this.chart.update();
    },
    createChart() {
      const chartCanvas = this.$refs.dailyBalanceGraphCanvas;

      this.chart = new Chart(chartCanvas, {
        type: 'line',
        data: {
          datasets: this.datasetsUsed(),
        },
        options: this.graphOptionsConfiguration(),
      });
    },
    graphOptionsConfiguration() {
      return {
        bezierCurve: true,
        elements: {
          line: {
            tension: 0.4,
          },
        },
        interaction: {
          mode: 'nearest', // Show dots on the nearest point during hover
          intersect: false,
          axis: 'x',
        },
        layout: {
          autoPadding: false, // removes padding applied to the right of the graph
          padding: {
            top: 50,
          },
        },
        maintainAspectRatio: false,
        plugins: {
          annotation: {
            annotations: this.annotationsConfiguration(),
          },
          legend: {
            display: false,
          },
          tooltip: this.tooltipConfiguration(),
        },
        pointHoverBorderColor: 'white',
        pointHoverBorderWidth: 2,
        pointHoverRadius: 6,
        responsive: true,
        scales: this.graphScalesConfiguration(),
      };
    },
    graphScalesConfiguration() {
      return {
        x: {
          border: {
            display: false,
          },
          display: true,
          grid: {
            display: false,
          },
          offset: false,
          ticks: { source: 'auto' },
          time: {
            unit: 'month',
            displayFormats: {
              month: 'MMM',
            },
          },
          type: 'time',
        },
        y: {
          min: minGraphValue(this.datasetsUsed()),
          beginAtZero: true,
          border: {
            display: false,
          },
          ticks: {
            callback: function (value, index, values) {
              return centsToCurrencyShorthand(value);
            },
            maxTicksLimit: 4,
          },
        },
      };
    },
    tooltipConfiguration() {
      return {
        enabled: false,
        external: this.customTooltipHandler,
      };
    },
    getAccountColour(accountIndex) {
      // TODO: get a full list of colours that we want to use
      const accountColours = [
        '#3e63dd', // indigo light 90
        '#0091FF', // primary light 90
        '#68DDFD', // sky light 90
        '#000',
        '#000',
        '#000',
        '#000',
        '#000',
      ];
      const colourIndex = accountIndex % accountColours.length;

      return accountColours[colourIndex];
    },
    annotationsConfiguration() {
      return {
        min_line: this.minLineAnnotation(),
        average_line: this.averageLineAnnotation(),
        max_line: this.maxLineAnnotation(),
      };
    },
    displayAnnotations() {
      return this.isAllAccountsViewSelected === false;
    },
    minLineAnnotation() {
      return {
        type: 'line',
        borderColor: constants.MIN_LINE_COLOR,
        borderDash: [6, 6],
        borderDashOffset: 0,
        borderWidth: 2,
        display: this.displayAnnotations(),
        label: {
          enabled: true,
          content: (ctx) => 'Min: ' + minDatasetValue(ctx.chart.data.datasets),
          position: 'end',
        },
        scaleID: 'y',
        value: (ctx) => minDatasetValue(ctx.chart.data.datasets),
      };
    },
    averageLineAnnotation() {
      return {
        type: 'line',
        borderColor: constants.AVERAGE_LINE_COLOR,
        borderDash: [6, 6],
        borderDashOffset: 0,
        borderWidth: 2,
        display: this.displayAnnotations(),
        label: {
          enabled: true,
          content: (ctx) =>
            'Average: ' + averageDatasetValue(ctx.chart.data.datasets),
          position: 'end',
        },
        scaleID: 'y',
        value: (ctx) => averageDatasetValue(ctx.chart.data.datasets),
      };
    },
    maxLineAnnotation() {
      return {
        type: 'line',
        borderColor: constants.MAX_LINE_COLOR,
        borderDash: [6, 6],
        borderDashOffset: 0,
        borderWidth: 2,
        display: this.displayAnnotations(),
        label: {
          enabled: true,
          content: (ctx) => 'Max: ' + maxDatasetValue(ctx.chart.data.datasets),
          position: 'end',
        },
        scaleID: 'y',
        value: (ctx) => maxDatasetValue(ctx.chart.data.datasets),
      };
    },
    handleMenuEvent(menuSelection) {
      switch (menuSelection) {
        case 'fullscreen':
          toggleFullscreen(this.$refs.dailyBalanceGraphCanvas);
          break;
        case 'print_chart':
          printChart(this.$refs.dailyBalanceGraphCanvas);
          break;
        case 'download_png':
          this.downloadChartImage('png');
          break;
        case 'download_jpeg':
          this.downloadChartImage('jpg');
          break;
        case 'download_pdf':
          this.downloadChartPdf();
          break;
      }
    },
    downloadChartImage(extension) {
      const canvas = this.$refs.dailyBalanceGraphCanvas;
      const fileName = this.getGraphFileName(extension);
      downloadChart(canvas, fileName, `image/${extension}`);
    },
    downloadChartPdf() {
      const extension = 'pdf';
      const canvas = this.$refs.dailyBalanceGraphCanvas;
      const fileName = this.getGraphFileName(extension);
      const chartDescription = 'Inscribe: Daily Balance Graph';
      downloadPDF(canvas, fileName, chartDescription);
    },
    getGraphFileName(extension) {
      let fileName = '';
      const timestamp = moment().format('YYYY-MM-DD');
      if (this.isAllAccountsViewSelected) {
        fileName = `dailyBalanceAllAccounts_${timestamp}`;
      } else if (this.isAggregateAccountViewSelected) {
        fileName = `dailyBalanceAggregateAccounts_${timestamp}`;
      } else {
        fileName = `dailyBalance_${this.selectedAccount.account_number}_${timestamp}`;
      }
      return `${fileName}.${extension}`;
    },
    fillRedWhenNegativeBalanceExists(context) {
      const chart = context.chart;
      const { ctx, chartArea, scales } = chart;

      if (!chartArea) {
        return null;
      }

      const chartHeight = chartArea.bottom - chartArea.top;
      const pointZeroPosition = scales.y.getPixelForValue(0);
      const pointZeroHeight = pointZeroPosition - chartArea.top;
      let fractionOfChartWithNegativeValues = pointZeroHeight / chartHeight;
      fractionOfChartWithNegativeValues = Math.min(
        Math.max(fractionOfChartWithNegativeValues, 0),
        1,
      ); // fix for edge cases where fractionOfChartWithNegativeValues is outside of 0-1 range

      const verticalGradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartHeight + chartArea.top,
      );

      if (this.isAllAccountsViewSelected) {
        verticalGradient.addColorStop(1, 'transparent');
      } else {
        verticalGradient.addColorStop(
          fractionOfChartWithNegativeValues,
          'transparent',
        ); // positive values
        verticalGradient.addColorStop(
          fractionOfChartWithNegativeValues,
          constants.FILL_COLOR_NEGATIVE_BALANCE,
        ); // negative values
      }

      return verticalGradient;
    },
    customTooltipHandler(context) {
      const { tooltip } = context;
      this.tooltipData = this.getTooltipData(tooltip);

      if (tooltip.opacity === 0) {
        this.showTooltip = false;
      } else {
        this.showTooltip = true;
        this.setTooltipPosition(tooltip);
      }
    },
    setTooltipPosition(tooltip) {
      let maxPoint = null;
      tooltip.dataPoints.forEach((dataPoint, i) => {
        if (maxPoint == null || dataPoint.parsed.y > maxPoint.parsed.y) {
          maxPoint = dataPoint;
        }
      });
      const {
        offsetLeft: chartOffsetLeft,
        offsetTop: chartOffsetTop,
        offsetHeight: chartHeight,
      } = this.chart.canvas;
      const marginBelowTooltip = 10;
      const tooltipElement = this.$refs.dailyBalanceGraphTooltipRef.$el;
      const pixelsFromTopOfChartToGraphPoint = maxPoint.element.y;
      const tooltipBottomPosition =
        chartOffsetTop +
        chartHeight +
        marginBelowTooltip -
        pixelsFromTopOfChartToGraphPoint;
      tooltipElement.style.left = chartOffsetLeft + tooltip.caretX + 'px';
      tooltipElement.style.bottom = tooltipBottomPosition + 'px';
    },
    getTooltipData(tooltip) {
      if (this.isAllAccountsViewSelected) {
        return this.getMultipleAccountTooltip(tooltip);
      } else {
        return this.getSingleAccountTooltip(tooltip);
      }
    },
    getMultipleAccountTooltip(tooltip) {
      const tooltipData = {
        title: tooltip.title,
        dataPoints: [],
        footer: null,
      };
      let total = 0;
      tooltip.dataPoints.forEach((dataPoint, i) => {
        const point = {
          label: dataPoint.dataset.label,
          date: dataPoint.raw.x,
          value: dataPoint.raw.y,
          color: dataPoint.dataset.borderColor,
          datasetIndex: dataPoint.dataset.datasetIndex,
          lineStyle: 'solid',
        };
        tooltipData.dataPoints.push(point);
        total += dataPoint.parsed.y;
      });
      tooltipData.footer = {
        label: 'Total',
        value: total,
      };
      return tooltipData;
    },
    getSingleAccountTooltip(tooltip) {
      const tooltipData = {
        title: tooltip.title,
        dataPoints: [],
        footer: null,
      };
      tooltipData.dataPoints.push({
        label: tooltip.dataPoints[0].dataset.label,
        date: tooltip.dataPoints[0].raw.x,
        value: tooltip.dataPoints[0].raw.y,
        color: tooltip.dataPoints[0].dataset.borderColor,
        datasetIndex: tooltip.dataPoints[0].dataset.datasetIndex,
        lineStyle: 'solid',
      });
      tooltipData.dataPoints.push({
        label: 'Average Balance',
        date: tooltip.dataPoints[0].raw.x,
        value: averageDatasetValue(this.chart.data.datasets),
        color: constants.AVERAGE_LINE_COLOR,
        datasetIndex: 2,
        lineStyle: 'dashed',
      });
      tooltipData.dataPoints.push({
        label: 'Maximum Balance',
        date: tooltip.dataPoints[0].raw.x,
        value: maxDatasetValue(this.chart.data.datasets),
        color: constants.MAX_LINE_COLOR,
        datasetIndex: 3,
        lineStyle: 'dashed',
      });
      tooltipData.dataPoints.push({
        label: 'Minimum Balance',
        date: tooltip.dataPoints[0].raw.x,
        value: minDatasetValue(this.chart.data.datasets),
        color: constants.MIN_LINE_COLOR,
        datasetIndex: 4,
        lineStyle: 'dashed',
      });

      return tooltipData;
    },
    setupEventListeners() {
      $event.$on('daily-balance-graph-menu-event', this.handleMenuEvent);
    },
    destroyEventListeners() {
      $event.$off('daily-balance-graph-menu-event', this.handleMenuEvent);
    },
  },
};
</script>

<style lang="scss" scoped>
.daily-balance-graph-canvas {
  background-color: $color-white;
}
</style>
